import { ChartBase } from 'components/common';
import { LabelItem } from 'components/segments';
import { Skeleton } from 'components/ui';
import { useMemo } from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';

export default function ChartDeviceTypes() {
  const { currentStore } = useAppSelector(userDetailsState);

  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);

  const { data, isFetching: loading } = useGetCustomerStatisticsQuery(
    {
      body: filtersApiPayload,
      params: {
        store_name: String(currentStore.completeInfo?.store_name),
        metric_list: ['order_device'],
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const deviceType = data?.order_device;

  const totalSum = useMemo(() => {
    if (!deviceType?.length) return 1;
    return deviceType?.reduce((acc, curr) => acc + curr.value, 0);
  }, [deviceType]);

  let desktop: number = 0;
  let mobile: number = 0;
  let tablet: number = 0;
  let unknown: number = 0;

  deviceType?.forEach((item) => {
    switch (item.device) {
      case 'desktop':
        if (item.value) desktop = item.value;
        break;
      case 'mobile':
        if (item.value) mobile = item.value;
        break;
      case 'tablet':
        if (item.value) tablet = item.value;
        break;
      case 'unknown':
        if (item.value) unknown = item.value;
        break;

      default:
        break;
    }
  });

  const chartData = useMemo(
    () => ({
      labels: ['Percentage'],
      datasets: [
        {
          label: 'Desktop',
          backgroundColor: '#125970',
          borderColor: '#125970',
          barThickness: 44,
          data: [desktop / totalSum],
        },
        {
          label: 'Mobile',
          backgroundColor: '#E2BE3B',
          borderColor: '#E2BE3B',
          barThickness: 44,
          data: [mobile / totalSum],
        },
        {
          label: 'Tablets',
          backgroundColor: '#C28573',
          borderColor: '#C28573',
          barThickness: 44,
          data: [tablet / totalSum],
        },
        {
          label: 'Unknown',
          backgroundColor: '#E1E1E1',
          borderColor: '#E1E1E1',
          barThickness: 44,
          data: [unknown / totalSum],
        },
      ],
    }),
    [desktop, mobile, tablet, totalSum, unknown]
  );

  const options = {
    indexAxis: 'y',
    interaction: {
      mode: 'nearest',
    },
    maintainAspectRatio: false,

    scales: {
      y: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  if (!!!loading && !!!deviceType) return null;

  return (
    <div className="mt-12">
      <div className="flex justify-between -mb-8 ">
        <p className="font-bold">Device types</p>
        {/* <p>Right Side</p> */}
      </div>

      <div className="-ml-2.5 relative">
        <ChartBase
          type="bar"
          // Passing empty array if loading to allow Skeleton to be shown
          data={loading ? [] : chartData}
          options={options}
        />
        {/* Positioning the Skeleton at same place absolutely, once the original chart size is bigger, note the negative margins */}
        <div className="absolute left-2.5 z-10 top-12 right-0.5">
          <Skeleton isLoaded={!loading}>
            <div className="h-11"></div>
          </Skeleton>
        </div>
      </div>
      <div className="flex -mt-10 space-x-24">
        <LabelItem
          color="graph_1"
          label="Desktop"
          highlightValue={(desktop / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          loading={loading}
        />
        <LabelItem
          color="graph_2"
          label="Mobile"
          highlightValue={(mobile / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          loading={loading}
        />
        <LabelItem
          color="graph_3"
          label="Tablet"
          highlightValue={(tablet / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          loading={loading}
        />
        <LabelItem
          color="gray"
          label="Unknown"
          highlightValue={(unknown / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          loading={loading}
        />
      </div>
    </div>
  );
}
